import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import { startRegistration } from '@simplewebauthn/browser';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { landingFor } from '../utils/landing';
import '../styles/PasskeyRegister.css';

interface PasskeyRegisterProps {
  onSkip?: () => void;
  onSuccess?: () => void;
}

const PasskeyRegister: React.FC<PasskeyRegisterProps> = ({ onSkip, onSuccess }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  // HOF-696 D5: without caller callbacks, land by the seat (a worker → /apps/labour).
  // Read optionally: the prompt must not demand an AuthProvider to render.
  const landing = landingFor(useContext(AuthContext)?.userProfile);

  const handleRegisterPasskey = async () => {
    try {
      setIsRegistering(true);
      setError(null);

      // Begin registration
      const { data: options } = await api.post('/passkeys/register/begin');

      // Use WebAuthn API to create credential
      const attResp = await startRegistration(options);

      // Complete registration
      const { data: verifyResp } = await api.post('/passkeys/register/complete', attResp);

      if (verifyResp.success) {
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(landing);
        }
      }
    } catch (err: any) {
      console.error('Passkey registration error:', err);
      if (err.name === 'NotAllowedError') {
        setError(t('passkey.errors.cancelled'));
      } else if (err.name === 'NotSupportedError') {
        setError(t('passkey.errors.notSupported'));
      } else {
        setError(t('passkey.errors.generic'));
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      navigate(landing);
    }
  };

  return (
    <div className="passkey-register">
      <div className="passkey-register-card">
        <div className="passkey-icon">🔐</div>
        <h2>{t('passkey.setupTitle')}</h2>
        <div className="passkey-explanation">
          <p>{t('passkey.explanation.biometricPrivacy')}</p>
          <p>{t('passkey.explanation.publicKeyOnly')}</p>
          <p>{t('passkey.explanation.summary')}</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="passkey-actions">
          <button
            onClick={handleRegisterPasskey}
            disabled={isRegistering}
            className="btn btn-primary btn-lg"
          >
            {isRegistering ? t('passkey.setupButtonProcessing') : t('passkey.setupButton')}
          </button>
          <button
            onClick={handleSkip}
            disabled={isRegistering}
            className="btn btn-link"
          >
            {t('passkey.setupLater')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasskeyRegister;
